import React, { memo, useEffect, useImperativeHandle, useRef } from "react";

import { ITransform, transformRect } from "../../transform";

import "./preview-overlay.css";
import { useIsSwapMode } from "../slideshow-preview/hooks/use-photo-swapping";
import { useSelectedZone } from "../../hooks/useSelection";

interface IOverlayZoneState {
	icon?: "add" | "add-disabled" | "pan" | "cross" | "photo" | "low-resolution" | undefined; // "bad-quality", "error" (rejected)
	// label: string;
	highlightZone?: boolean;
}

export interface IPreviewOverlayRef {
	el: HTMLDivElement;

	/**
	 * Add an overlay for each zone of this spread
	 * We don't want to add/update a div for all the zones in the definition, so ideally we only add the spreads in view.
	 */
	addSpread(spread: eXpress.core.Spread, transform: ITransform): void;

	/** Remove zone overlays of a spread */
	removeSpread(spread: eXpress.core.Spread): void;

	/** Returns true if overlay information is available for this spread */
	hasSpread(spread: eXpress.core.Spread): boolean;

	// Update the overlay layout of a spread
	updateSpreadLayout(spread: eXpress.core.Spread, transform: ITransform): void;

	updateZoneLayout(zone: eXpress.core.IZone, transform: ITransform): void;

	/** Hide all zones for a spread (example: when spread is not yet loaded or in error) */
	hideSpread(spread: eXpress.core.Spread, shouldHide: boolean): void;

	/** Update overlay state of a zone, partial updates are allowed */
	updateZoneState(zone: eXpress.core.IZone | undefined, state: IOverlayZoneState): void;
}

interface IZoneToOverlayMapItem {
	el: HTMLElement;
	state: IOverlayZoneState;
}

type ZoneToOverlayMap = Map<eXpress.core.IZone, IZoneToOverlayMapItem>;

function createPreviewOverlay(element: HTMLDivElement | null | undefined) {
	eXpress.utils.assertIsNullOrUndefined(
		element,
		"createPreviewOverlay element argument must be defined",
	);

	/** Hold a list of all spread divs on screen */
	const spreadsToZonesDiv = new Map<eXpress.core.Spread, ZoneToOverlayMap>();

	/** Create an overlay for a zone, no layout */
	function createZone(zone: eXpress.core.IZone, zoneToInfo: ZoneToOverlayMap) {
		eXpress.utils.assertIsNullOrUndefined(element, "Expected a PreviewOverlay root element");
		const zoneInfo = zoneToInfo.get(zone);
		if (zoneInfo) return zoneInfo;

		const zoneEl = document.createElement("div");
		zoneEl.classList.add("zone-overlay");
		const newZoneInfo = {
			el: zoneEl,
			state: {},
		};
		zoneToInfo.set(zone, newZoneInfo);
		element.appendChild(zoneEl);
		return newZoneInfo;
	}

	/** Delete an overlay for a zone */
	function deleteZone(zone: eXpress.core.IZone, zoneToInfo: ZoneToOverlayMap) {
		const zoneInfo = zoneToInfo.get(zone);
		if (zoneInfo) {
			zoneInfo.el.remove();
			zoneToInfo.delete(zone);
		}
	}

	/** Update the layout (position/size) of an overlay for a zone */
	function updateZoneLayoutInternal(
		zone: eXpress.core.IZone,
		el: HTMLElement,
		transform: ITransform,
	) {
		// Update all Zones
		const rect = transformRect(zone, transform);
		el.style.left = `${rect.left}px`;
		el.style.top = `${rect.top}px`;
		el.style.width = `${rect.width}px`;
		el.style.height = `${rect.height}px`;
		el.style.zIndex = `${zone.index}`;
		el.style.transform = zone.angle ? `rotate(${zone.angle}deg)` : "";
	}

	/** Update the layout (position/size) of an overlay for a zone */
	function hideZone(zoneEl: HTMLElement, shouldHide: boolean) {
		zoneEl.style.opacity = shouldHide ? "0" : "1";
	}

	/** Get the zones of a spread to create overlays for */
	function getZones(spread: eXpress.core.Spread) {
		// Currenly only add for Text Zones and Photo Zones
		return spread.zones.filter(
			(zone) =>
				eXpress.core.isAnyTextZone(zone) ||
				eXpress.core.isPhotoZone(zone) ||
				eXpress.core.isPageZone(zone) ||
				eXpress.core.isSymbolZone(zone),
		);
	}

	/** Update the layout of a spread (add/removing zones if needed) */
	function updateSpreadLayout(spread: eXpress.core.Spread, transform: ITransform) {
		eXpress.utils.assertIsNullOrUndefined(element, "Expected a PreviewOverlay root element");
		const zoneToInfo =
			spreadsToZonesDiv.get(spread) || new Map<eXpress.core.IZone, IZoneToOverlayMapItem>();

		const { onlyLeft, onlyRight, both } = eXpress.collections.arrays.diffArrays(
			[...zoneToInfo.keys()], // previous zones
			// Currenly only add for Text Zones and Photo Zones
			getZones(spread), // new zones
		);

		// Remove unused zones
		onlyLeft.forEach((zone) => deleteZone(zone, zoneToInfo));

		// Update existing zones
		both.forEach((zone) => {
			const zoneInfo = eXpress.utils.assert(
				zoneToInfo.get(zone),
				"Expected an element in the overlay map",
			);
			updateZoneLayoutInternal(zone, zoneInfo.el, transform);
		});

		// Add new zones
		onlyRight.forEach((zone) => {
			const zoneInfo = createZone(zone, zoneToInfo);
			updateZoneLayoutInternal(zone, zoneInfo.el, transform);
		});

		spreadsToZonesDiv.set(spread, zoneToInfo);
	}

	function updateZoneLayout(zone: eXpress.core.Zone, transform: ITransform) {
		eXpress.utils.assertIsNullOrUndefined(element, "Expected a PreviewOverlay root element");
		const zoneToInfo =
			spreadsToZonesDiv.get(zone.spread) ||
			new Map<eXpress.core.IZone, IZoneToOverlayMapItem>();
		const zoneInfo = eXpress.utils.assert(
			zoneToInfo.get(zone),
			"Expected an element in the overlay map",
		);
		updateZoneLayoutInternal(zone, zoneInfo.el, transform);
	}

	/** Add overlay for all zones of the spread */
	function addSpread(spread: eXpress.core.Spread, transform: ITransform) {
		const zoneToInfo =
			spreadsToZonesDiv.get(spread) || new Map<eXpress.core.IZone, IZoneToOverlayMapItem>();
		spreadsToZonesDiv.set(spread, zoneToInfo);

		updateSpreadLayout(spread, transform);
	}

	/** Remove all zone overlays for this spread */
	function removeSpread(spread: eXpress.core.Spread) {
		const zoneToInfo = spreadsToZonesDiv.get(spread);
		if (!zoneToInfo) return;

		for (const zone of zoneToInfo.keys()) {
			deleteZone(zone, zoneToInfo);
		}

		spreadsToZonesDiv.delete(spread);
	}

	/** Return true if overlay information for this spread is added */
	function hasSpread(spread: eXpress.core.Spread) {
		return !!spreadsToZonesDiv.get(spread);
	}

	/** Hide all zones for a spread (example: when spread is not yet loaded or in error) */
	function hideSpread(spread: eXpress.core.Spread, shouldHide: boolean) {
		const zoneToInfo = spreadsToZonesDiv.get(spread);
		if (!zoneToInfo) return;

		for (const zoneInfo of zoneToInfo.values()) {
			hideZone(zoneInfo.el, shouldHide);
		}
	}

	const updateZoneState: IPreviewOverlayRef["updateZoneState"] = (zone, state) => {
		if (!zone) return;
		const zoneToInfo = spreadsToZonesDiv.get(zone.spread);
		if (!zoneToInfo) return;

		const zoneInfo = zoneToInfo.get(zone);
		if (!zoneInfo) return;

		// Merge new state with existing state
		// This allows us for example to highlight with keeping the existing state
		zoneInfo.state = {
			...zoneInfo.state,
			...state,
		};

		// Currently we only work with CSS classes,
		// if needed we could also add DOM (like buttons with :active state on click)
		zoneInfo.el.classList.toggle("zone-overlay--add-icon", zoneInfo.state.icon === "add");
		zoneInfo.el.classList.toggle(
			"zone-overlay--add-disabled-icon",
			zoneInfo.state.icon === "add-disabled",
		);
		zoneInfo.el.classList.toggle("zone-overlay--pan-icon", zoneInfo.state.icon === "pan");
		zoneInfo.el.classList.toggle("zone-overlay--cross-icon", zoneInfo.state.icon === "cross");
		zoneInfo.el.classList.toggle("zone-overlay--highlight", !!zoneInfo.state.highlightZone); // Make boolean!
		zoneInfo.el.classList.toggle(
			"zone-overlay--resolution-icon",
			zoneInfo.state.icon === "low-resolution",
		);
	};

	return {
		el: element,
		addSpread,
		removeSpread,
		hasSpread,
		updateSpreadLayout,
		updateZoneLayout,
		hideSpread,
		updateZoneState,
	};
}

/** Overlay for zones with warning, pan, move, add, ... icons */
export const PreviewOverlay = memo(({ ref }: { ref?: React.Ref<IPreviewOverlayRef | null> }) => {
	/** Ref to DOM element */
	const divRef = useRef<HTMLDivElement>(null);

	/** Local Ref to store the instantiated third-party object */
	const overlayRef = useRef<IPreviewOverlayRef | null>(null);

	const { isSwapModeActive } = useIsSwapMode();
	const selectedZone = useSelectedZone();

	/** * Helper to safely get or create the overlay instance once the DOM is ready.
	 * This ensures it is only created once, and always has access to the DOM ref.
	 */
	const getOverlay = (): IPreviewOverlayRef => {
		if (!divRef.current) {
			throw new Error(
				"PreviewOverlay: Attempted to access overlay before DOM node was mounted.",
			);
		}

		if (!overlayRef.current) {
			overlayRef.current = createPreviewOverlay(divRef.current);
		}
		return overlayRef.current;
	};

	useEffect(() => {
		const previewOverlay = getOverlay();
		if (!previewOverlay) return;

		const spreadZones = selectedZone?.spread
			?.getFillablePhotoZones(false, "photo")
			?.filter((z) => z !== selectedZone);

		spreadZones?.forEach((z) => {
			previewOverlay.updateZoneState(z, {
				highlightZone: isSwapModeActive,
			});
		});
	}, [isSwapModeActive, selectedZone]);

	/** Expose the PreviewOverlay object via ref to the parent component */
	useImperativeHandle(ref, () => getOverlay(), []);

	return <div className="preview-overlay" ref={divRef} />;
});
PreviewOverlay.displayName = "PreviewOverlay";
